/**
 * Recent proofs store — Zustand, persisted to localStorage.
 *
 * Phase: Vault — the **server** at `/api/v1/proofs` is now the
 * source of truth (see `hooks/useProofs.ts`). This store is kept
 * as the **offline cache layer**: reads in `useProofs` fall back
 * to this slice when the API is unreachable, and successful
 * refetches mirror the server response back via [`replaceAll`].
 * The Sign-a-File / Prove-Idea wizards still call [`add`] for an
 * instant local update; the mutation hook also POSTs to the server
 * so cache and server stay aligned.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ProofKind = 'file' | 'idea' | 'invoice' | 'contract' | 'memo' | 'document';

export interface Proof {
  /** Stable client-side id. */
  id: string;
  /** What was signed. */
  kind: ProofKind;
  /** Display name (filename, first line of idea, document title…). */
  name: string;
  /** Hex-encoded SHA-256 of the canonical content. */
  hash: string;
  /** ISO 8601 UTC timestamp at sign time. */
  timestamp: string;
  /** Email of the signer (the logged-in user). */
  signer: string;
  /** Schema version for forward compatibility. */
  version: string;
  /** Optional original content size in bytes (files only). */
  bytes?: number;
  /** Optional user-supplied description. Stored locally and in the
   *  certificate download; the server proof endpoint does not yet
   *  carry this field (Phase 4.9 follow-up). */
  description?: string;
  /** Original filename when `kind === 'file'`, before any user rename. */
  original_filename?: string;
}

const PROOF_VERSION = '1.0.0';
const MAX_PROOFS = 50;

interface ProofsState {
  proofs: Proof[];
  add: (
    input:
      | {
          kind: ProofKind;
          name: string;
          hash: string;
          signer: string;
          bytes?: number;
          description?: string;
          original_filename?: string;
        }
      | Proof,
  ) => Proof;
  remove: (id: string) => void;
  /** Replace the entire local cache with a server-authoritative
   *  snapshot. Called by `useProofs` after a successful refetch so
   *  the offline cache always trails the server view by at most
   *  one round-trip. */
  replaceAll: (proofs: Proof[]) => void;
  /** Merge a server-authoritative snapshot into the local cache
   *  without dropping local-only proofs. The server list takes
   *  precedence for ids it owns (latest fields win); ids present
   *  only locally — typically a freshly-signed proof whose POST
   *  has not yet landed — are preserved so they stay visible in
   *  the UI and remain available for the migration hook to
   *  retry-push. Result is sorted newest-first by timestamp and
   *  capped at `MAX_PROOFS`. */
  mergeServer: (proofs: Proof[]) => void;
  clear: () => void;
}

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `p_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export const useProofsStore = create<ProofsState>()(
  persist(
    (set, get) => ({
      proofs: [],
      add: (input) => {
        // Accepts either a draft (the legacy wizard shape) or a
        // fully-formed Proof (server round-trip). The `'id' in input`
        // guard distinguishes the two so the wizard path keeps its
        // auto-id / auto-timestamp behavior while the server-mirror
        // path preserves authoritative ids.
        let proof: Proof;
        if ('id' in input) {
          proof = input;
        } else {
          // Build the proof without ever assigning `bytes: undefined` —
          // exactOptionalPropertyTypes refuses that.
          const base: Proof = {
            id: newId(),
            kind: input.kind,
            name: input.name,
            hash: input.hash,
            timestamp: new Date().toISOString(),
            signer: input.signer,
            version: PROOF_VERSION,
          };
          if (input.bytes !== undefined) base.bytes = input.bytes;
          if (input.description !== undefined) base.description = input.description;
          if (input.original_filename !== undefined) base.original_filename = input.original_filename;
          proof = base;
        }
        // Dedupe by id so re-adding from a server mirror doesn't
        // double-list a proof the wizard already inserted.
        const existing = get().proofs.filter((p) => p.id !== proof.id);
        const next = [proof, ...existing].slice(0, MAX_PROOFS);
        set({ proofs: next });
        return proof;
      },
      remove: (id) => set({ proofs: get().proofs.filter((p) => p.id !== id) }),
      replaceAll: (proofs) => set({ proofs: proofs.slice(0, MAX_PROOFS) }),
      mergeServer: (proofs) => {
        const serverIds = new Set(proofs.map((p) => p.id));
        const localOnly = get().proofs.filter((p) => !serverIds.has(p.id));
        const merged = [...proofs, ...localOnly];
        merged.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
        set({ proofs: merged.slice(0, MAX_PROOFS) });
      },
      clear: () => set({ proofs: [] }),
    }),
    {
      name: 'gi-engine.proofs',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ proofs: state.proofs }),
    },
  ),
);

/** Build the JSON object users download as their proof certificate. */
export function buildProofCertificate(proof: Proof) {
  const cert: Record<string, unknown> = {
    filename: proof.name,
    hash: proof.hash,
    timestamp: proof.timestamp,
    signer: proof.signer,
    version: proof.version,
    kind: proof.kind,
    bytes: proof.bytes,
  };
  if (proof.description) cert.description = proof.description;
  if (proof.original_filename && proof.original_filename !== proof.name) {
    cert.original_filename = proof.original_filename;
  }
  return cert;
}
